import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

interface CollapsibleCardProps {
  title: string;
  icon?: LucideIcon;
  iconClassName?: string;
  defaultOpen?: boolean;
  /** Contingut opcional a la dreta de la capçalera (ex: un botó "Nou X").
   *  Accepta una funció si depèn de l'estat obert/tancat. */
  headerActions?: ReactNode | ((open: boolean) => ReactNode);
  className?: string;
  children: ReactNode;
  /** Sobreescriu l'estat intern (obert/tancat) mentre no sigui undefined —
   *  útil per forçar totes les targetes obertes abans d'imprimir. */
  forceOpen?: boolean;
  /** Nombre d'elements que conté la targeta (llista, taula...). Si és > 0
   *  es mostra com "(N)" just després del títol. */
  count?: number;
  /** Punt vermell just després del títol (p.ex. "hi ha contingut nou que
   *  encara no s'ha vist") — es manté visible tant oberta com tancada. */
  showNewIndicator?: boolean;
  /** Es crida amb el nou estat (obert/tancat) cada cop que l'usuari clica la
   *  capçalera — útil per marcar com "vist" en obrir. */
  onToggle?: (open: boolean) => void;
}

/**
 * Targeta colapsable de dues columnes — mateix patró visual usat a
 * ExpedientComercialForm.tsx i BudgetForm.tsx: capçalera amb icona de
 * chevron + títol, contingut nomes renderitzat quan està oberta.
 * Pensat per anar a la columna dreta d'un layout `grid grid-cols-2 gap-6`
 * on l'esquerra porta les dades principals sempre visibles.
 */
export function CollapsibleCard({
  title,
  icon: Icon,
  iconClassName = 'text-gray-600',
  defaultOpen = false,
  headerActions,
  className,
  children,
  forceOpen,
  count,
  showNewIndicator,
  onToggle,
}: CollapsibleCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = forceOpen ?? open;
  const actions = typeof headerActions === 'function' ? headerActions(isOpen) : headerActions;

  const toggle = () => {
    const next = !isOpen;
    setOpen(next);
    onToggle?.(next);
  };

  return (
    <div className={cn('bg-white border border-gray-200 rounded-lg', className)}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <button
          type="button"
          onClick={toggle}
          className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity"
        >
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {Icon && <Icon size={16} className={iconClassName} />}
          {title}
          {showNewIndicator && (
            <span className="h-2 w-2 rounded-full bg-red-500" title="Hi ha contingut nou" />
          )}
          {typeof count === 'number' && count > 0 && (
            <span className="text-xs font-normal text-muted-foreground">({count})</span>
          )}
        </button>
        {actions}
      </div>
      {isOpen && children}
    </div>
  );
}
